import { useBlocker, useNavigate } from "@tanstack/react-router";
import { type RefObject, useEffect, useRef, useState } from "react";
import { z } from "zod";

/**
 * Taslak koruma (Prompt 04): kaydedilmemiş değişiklik varken sayfadan ayrılmayı engeller.
 * Tarayıcı sekmesini kapatma/yenileme → `beforeunload`; uygulama içi gezinme → yönlendirici engeli.
 */
export function useDraftGuard(
  isDirty: boolean,
  message = "Kaydedilmemiş değişiklikleriniz var. Sayfadan ayrılmak istediğinize emin misiniz?",
): void {
  useBlocker({ shouldBlockFn: () => isDirty && !window.confirm(message), enableBeforeUnload: isDirty });
}

/** Ctrl/⌘+Enter formu gönderir (çok satırlı alanlarda Enter yeni satır demektir). */
export function useSubmitShortcut(formRef: RefObject<HTMLFormElement | null>): void {
  useEffect(() => {
    const form = formRef.current;
    if (!form) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        form.requestSubmit();
      }
    };
    form.addEventListener("keydown", onKeyDown);
    return () => form.removeEventListener("keydown", onKeyDown);
  }, [formRef]);
}

/** Genel kısayol: `mod+k` = Ctrl+K / ⌘K. Yazı alanındayken yalnızca değiştirici tuşlu kısayollar çalışır. */
export function useGlobalShortcut(combo: `mod+${string}`, handler: () => void): void {
  useEffect(() => {
    const key = combo.slice(4).toLowerCase();
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === key) {
        event.preventDefault();
        handler();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [combo, handler]);
}

export function useDocumentTitle(title: string): void {
  useEffect(() => {
    document.title = `${title} · İstasyon Platformu`;
  }, [title]);
}

/** Yazarken her tuşta istek atmamak için değeri `delayMs` sonra yansıtır (arama kutuları). */
export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(handle);
  }, [value, delayMs]);
  return debounced;
}

/**
 * Anında kaydedilen onay kutuları için iyimser durum. Seçim, olay işleyicinin İÇİNDE (eşzamanlı) yerel duruma yazılır;
 * yalnızca mutasyon durumuna güvenilirse React denetimli kutuyu bir an için eski değere döndürür (titreme).
 * Sunucu verisi yenilenince `clear` ile yerel değer atılır ve gerçek durum gösterilir.
 */
export function useOptimisticFlags(): {
  value: (key: string, serverValue: boolean) => boolean;
  set: (key: string, next: boolean) => void;
  clear: (key: string) => void;
} {
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  return {
    value: (key, serverValue) => flags[key] ?? serverValue,
    set: (key, next) => setFlags((current) => ({ ...current, [key]: next })),
    clear: (key) =>
      setFlags((current) => {
        const { [key]: _removed, ...rest } = current;
        return rest;
      }),
  };
}

/** Hızlı işlemden gelişin arama parametresi (`?yeni=1`); sayfaların arama şemasına eklenir. */
export const quickActionSearch = { yeni: z.literal("1").optional().catch(undefined) };

/**
 * Hızlı işlemden ya da "Yeni kayıt" menüsünden gelindiyse (`?yeni=1`, ADR 0027) formu BİR KEZ açar ve işareti adresten
 * siler — sayfa yenilenince ya da geri gelince form kendiliğinden yeniden açılmaz. `ready`, düğmenin görünme koşuludur
 * (izin, istasyon seçimi, yüklenme): koşul sağlanmadıkça form açılmaz.
 */
export function useOpenFromQuickAction(requested: boolean, open: () => void, ready = true): void {
  const navigate = useNavigate();
  const handled = useRef(false);
  useEffect(() => {
    if (!requested || !ready || handled.current) return;
    handled.current = true;
    open();
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) => ({ ...previous, yeni: undefined })) as never,
      replace: true,
    });
  }, [requested, ready, open, navigate]);
}
